import cookieParser from 'cookie';
import Cookie from 'js-cookie';

import { CAMPAIGN_COOKIE_NAMES, CAMPAIGN_QUERY_PARAMS } from 'shared-utils';

// Bounds the over-attribution the last-touch rule allows: a campaign-free arrival leaves a stored
// campaign in place, so this window is how long an organic visit can still be credited to it.
const CAMPAIGN_COOKIE_MAX_AGE_DAYS = 14;
const CAMPAIGN_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * CAMPAIGN_COOKIE_MAX_AGE_DAYS;

// Long enough for anything a campaign puts in one, short enough that a hand-crafted URL cannot
// push a multi-kilobyte value into every subsequent request header.
const MAX_VALUE_LENGTH = 200;

export const isValidCampaignValue = (value) =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_VALUE_LENGTH;

/**
 * Persist the GA4 campaign parameters a marketing link arrives with, so they still exist at the
 * end of the funnel.
 *
 * GA4 reads them off `location.search` on the first page_view and attributes the session, but the
 * storefront builds every internal URL from scratch, so by the checkout the parameters are gone
 * and nothing can attribute the ORDER to the campaign that produced it.
 *
 * LAST TOUCH, AND ALL OR NOTHING. A link that carries any campaign parameter replaces the whole
 * set, clearing the ones it does not carry. Merging instead would let a visitor arriving from a
 * second campaign be attributed to a source from the first — one order credited to a campaign
 * that never sent it, which is worse than no attribution at all.
 */
const captureCampaignParams = (ctx) => {
  const query = ctx.query ?? {};
  const arriving = CAMPAIGN_QUERY_PARAMS.some((param) => isValidCampaignValue(query[param]));

  // No campaign parameters on this URL: leave whatever an earlier landing stored alone. Writing
  // empty cookies here would turn every ordinary visit into a blank campaign.
  if (!arriving) {
    return;
  }

  const isServer = !!ctx.req;
  const setCookieHeader = [];

  CAMPAIGN_QUERY_PARAMS.forEach((param) => {
    const name = CAMPAIGN_COOKIE_NAMES[param];
    const value = query[param];
    const keep = isValidCampaignValue(value);

    if (isServer) {
      setCookieHeader.push(
        keep
          ? cookieParser.serialize(name, value, {
              path: '/',
              maxAge: CAMPAIGN_COOKIE_MAX_AGE_SECONDS,
              secure: process.env.NODE_ENV === 'production',
              sameSite: 'lax',
            })
          : // Expire rather than skip, so a parameter this landing omits cannot survive from the
            // previous one — see the all-or-nothing rule above.
            cookieParser.serialize(name, '', {
              path: '/',
              maxAge: 0,
              secure: process.env.NODE_ENV === 'production',
              sameSite: 'lax',
            })
      );
    } else if (keep) {
      Cookie.set(name, value, {
        path: '/',
        expires: CAMPAIGN_COOKIE_MAX_AGE_DAYS,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',
      });
    } else {
      Cookie.remove(name, { path: '/' });
    }
  });

  if (isServer) {
    const existingCookies = ctx.res.getHeader('Set-Cookie');
    const existing = existingCookies
      ? Array.isArray(existingCookies)
        ? [...existingCookies]
        : [existingCookies]
      : [];
    ctx.res.setHeader('Set-Cookie', [...existing, ...setCookieHeader]);
  }
};

export default captureCampaignParams;
